import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Button, TimePicker, Upload, List, Modal, Tag, Tooltip, Space } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, FileOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import Input from '../../components/input/Input';
import InputNumber from '../../components/input/InputNumber';
import Select from '../../components/selects/Select';
import DateComponent from '../../components/date/Date';
import api from '../../services/api';
import { displayMessage } from '../../helpers/messages';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import dayjs from 'dayjs';

const { confirm } = Modal;

const DetailBatch = () => {
  const navigate = useNavigate();
  const { idBatch } = useParams();
  const { setShowLoading, loggedUser } = useContext(GeneralContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [clients, setClients] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // Arquivos pendentes para novo lote

  useEffect(() => {
    loadClients();
    if (idBatch && idBatch !== 'new') {
      setIsNew(false);
      loadBatch();
      loadAttachments();
    } else {
      setIsNew(true);
      // Preencher data e hora atual para novo lote
      const now = dayjs();
      form.setFieldsValue({
        date: now.format('YYYY-MM-DD'),
        time: now,
      });
    }
  }, [idBatch]);

  const loadClients = async () => {
    try {
      const response = await api.client.list();
      setClients(response.data);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_LOAD'));
    }
  };

  const loadBatch = async () => {
    setLoading(true);
    try {
      const response = await api.batch.get(idBatch);
      const batch = response.data;
      
      form.setFieldsValue({
        ...batch,
        time: batch.time ? dayjs(batch.time, 'HH:mm:ss') : null,
      });
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_LOAD'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const dataToSend = {
        ...values,
        time: values.time ? values.time.format('HH:mm:ss') : null,
      };

      if (isNew) {
        // Criar o lote
        const response = await api.batch.create(dataToSend);
        
        // O backend retorna { batch: {...} }, então o ID está em batch.id
        const newBatchId = response.data.batch?.id || response.data.id;

        // Se houver arquivos pendentes, fazer upload
        if (pendingFiles.length > 0 && newBatchId) {
          try {
            await uploadPendingFiles(newBatchId);
            showSuccessMessage(`Lote criado e ${pendingFiles.length} anexo(s) adicionado(s) com sucesso!`);
          } catch (uploadError) {
            showErrorMessage('Lote criado, mas houve erro ao enviar alguns anexos');
          }
        } else {
          showSuccessMessage(displayMessage('SUCCESS_CREATE'));
        }
      } else {
        await api.batch.update(idBatch, dataToSend);
        showSuccessMessage(displayMessage('SUCCESS_UPDATE'));
      }
      
      navigate('/batches');
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_GENERIC'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/batches');
  };

  const handleDelete = () => {
    confirm({
      title: 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: 'Deseja realmente excluir este lote? Esta ação não pode ser desfeita.',
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setShowLoading(true);
          await api.batch.delete(idBatch);
          showSuccessMessage(displayMessage('SUCCESS_DELETE'));
          navigate('/batches');
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_DELETE'));
        } finally {
          setShowLoading(false);
        }
      },
    });
  };

  const loadAttachments = async () => {
    try {
      const response = await api.batch.listAttachments(idBatch);
      setAttachments(response.data);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao carregar anexos');
    }
  };

  const handleUploadAttachment = async ({ file, onSuccess, onError }) => {
    try {
      setUploadingFiles(true);
      
      const formData = new FormData();
      const fileToUpload = file.originFileObj || file;
      formData.append('files', fileToUpload);

      const response = await api.batch.uploadAttachment(idBatch, formData);
      
      showSuccessMessage('Anexo enviado com sucesso!');
      await loadAttachments();
      
      if (onSuccess) {
        onSuccess(response.data, file);
      }
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao enviar anexo');
      
      if (onError) {
        onError(error);
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteAttachment = (attachment) => {
    confirm({
      title: 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: `Deseja realmente excluir o anexo "${attachment.file}"?`,
      okText: 'Sim',
      okType: 'danger',
      cancelText: 'Não',
      onOk: async () => {
        try {
          await api.batch.deleteAttachment(attachment.id);
          showSuccessMessage('Anexo excluído com sucesso!');
          loadAttachments();
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || 'Erro ao excluir anexo');
        }
      },
    });
  };

  const handleDownloadAttachment = (attachment) => {
    if (attachment.fullUrl) {
      window.open(attachment.fullUrl, '_blank');
    } else {
      showErrorMessage('URL de download não encontrada');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Função para adicionar arquivos pendentes (novo lote)
  const handleAddPendingFile = (file) => {
    const fileObj = file.originFileObj || file;
    
    if (!fileObj.uid) {
      fileObj.uid = `${Date.now()}-${fileObj.name}`;
    }
    
    setPendingFiles(prev => [...prev, fileObj]);
    return false; // Impedir upload automático
  };

  // Função para remover arquivo pendente
  const handleRemovePendingFile = (fileToRemove) => {
    setPendingFiles(prev => prev.filter(f => f.uid !== fileToRemove.uid && f.name !== fileToRemove.name));
  };

  // Função para fazer upload dos arquivos pendentes após criar o lote
  const uploadPendingFiles = async (batchId) => {
    const uploadPromises = pendingFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('files', file);
      return api.batch.uploadAttachment(batchId, formData);
    });

    return await Promise.all(uploadPromises);
  };

  const clientOptions = clients
    .filter(c => c.is_active)
    .map(client => ({
      value: client.id,
      label: client.company_name,
    }));

  return (
    <Card
      title={isNew ? displayMessage('NEW_BATCH') : displayMessage('EDIT_BATCH')}
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          {displayMessage('BACK')}
        </Button>
      }
    >
      <Form
        form={form}
        onFinish={handleSave}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={displayMessage('CLIENT')}
              name="client_id"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <Select
                placeholder="Selecione o cliente"
                options={clientOptions}
                showSearch
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={6}>
            <Form.Item
              label={displayMessage('DATE')}
              name="date"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <DateComponent placeholder="Selecione a data" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={6}>
            <Form.Item
              label={displayMessage('TIME')}
              name="time"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="Selecione a hora" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={displayMessage('INVOICE_NUMBER')}
              name="invoice_number"
              rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
            >
              <Input placeholder="Digite o número da nota fiscal" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={displayMessage('DRUM_QUANTITY')}
              name="drum_quantity"
              rules={[
                { required: true, message: displayMessage('ERROR_REQUIRED_FIELD') },
                { type: 'number', min: 1, message: 'Quantidade deve ser maior que zero' }
              ]}
            >
              <InputNumber placeholder="Digite a quantidade de tambores" min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label={displayMessage('OBSERVATION')}
              name="observation"
            >
              <Input
                type="textarea"
                placeholder="Digite observações (opcional)"
                rows={4}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space>
            <Button onClick={handleBack}>
              {displayMessage('CANCEL')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={loading}
            >
              {displayMessage('SAVE')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      
      {/* Seção de Anexos - aparece sempre */}
      {isNew ? (
        // Novo Lote - permite selecionar arquivos que serão enviados após criar
        <Card 
          title={`Anexos${pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ''}`}
          style={{ marginTop: 16 }}
        >
          <Upload
            multiple
            fileList={pendingFiles.map((file, index) => ({
              uid: file.uid || `file-${index}`,
              name: file.name || 'arquivo',
              status: 'done',
              size: file.size || 0,
            }))}
            beforeUpload={handleAddPendingFile}
            onRemove={handleRemovePendingFile}
            showUploadList={{
              showDownloadIcon: false,
              showRemoveIcon: true,
            }}
          >
            <Button icon={<UploadOutlined />}>
              Selecionar Arquivos
            </Button>
          </Upload>
          <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
            {pendingFiles.length > 0 
              ? `${pendingFiles.length} arquivo(s) será(ão) anexado(s) automaticamente após criar o lote.`
              : 'Selecione arquivos para anexar ao lote após criá-lo.'
            }
          </div>
        </Card>
      ) : (
        // Editando Lote - upload direto
        (
        <Card 
          title="Anexos" 
          style={{ marginTop: 16 }}
          extra={
            <Upload
              customRequest={handleUploadAttachment}
              showUploadList={false}
              multiple
            >
              <Button icon={<UploadOutlined />} loading={uploadingFiles}>
                Adicionar Anexo
              </Button>
            </Upload>
          }
        >
          <List
            dataSource={attachments}
            locale={{ emptyText: 'Nenhum anexo adicionado ainda' }}
            renderItem={(attachment) => (
              <List.Item
                actions={[
                  <Tooltip title="Baixar anexo">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      size="small"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      Download
                    </Button>
                  </Tooltip>,
                  <Tooltip title="Excluir anexo">
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => handleDeleteAttachment(attachment)}
                    >
                      Excluir
                    </Button>
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                  title={
                    <Space>
                      <span>{attachment.file}</span>
                      <Tag color="blue">{formatFileSize(attachment.size)}</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <span>
                        <strong>Data:</strong> {dayjs(attachment.createdAt).format('DD/MM/YYYY')} às {dayjs(attachment.createdAt).format('HH:mm')}
                      </span>
                      <span>
                        <strong>Anexado por:</strong> {attachment.user ? `${attachment.user.first_name} ${attachment.user.last_name}` : 'N/A'}
                      </span>
                      {attachment.description && (
                        <span>
                          <strong>Descrição:</strong> {attachment.description}
                        </span>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
        )
      )}

      {/* Botão de excluir lote - apenas para lotes existentes */}
      {!isNew && (
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            style={{ minWidth: 200 }}
          >
            Excluir Lote Permanentemente
          </Button>
          <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
            Esta ação não pode ser desfeita
          </div>
        </div>
      )}
    </Card>
  );
};

export default DetailBatch;



